import { useEffect, useRef } from 'react';

import { useNotifyStripeError, useStripePaymentElement } from '@order/integrations-stripe';

import { type useSweetpassCheckoutMachine } from '../useSweetpassCheckoutMachine';

// ─────────────────────────────────────────────────────────────────────────────

export const useSweetpassPaymentElement = (params: UseSweetpassPaymentElementParams) => {
  const { amount, stripeIntentConfig, stripeElementConfig, setConfirmStripe } = params;

  // ─── Stripe ──────────────────────────────────────────────────────────

  // The SDK reads its configs once, on mount.
  const stripeIntentConfigRef = useRef(stripeIntentConfig);
  const stripeElementConfigRef = useRef(stripeElementConfig);

  const { embeddedPaymentElementView, stripeError, isUpdatingTotal, confirmStripe } =
    useStripePaymentElement({
      total: amount,
      paymentMethods,
      stripeIntentConfigRef,
      stripeElementConfigRef,
    });

  // ─── Machine Binding ─────────────────────────────────────────────────

  // `confirmStripe` changes once Stripe loads on web, so it's re-registered.
  useEffect(() => {
    setConfirmStripe(confirmStripe);
  }, [confirmStripe, setConfirmStripe]);

  // ─── Feedback ────────────────────────────────────────────────────────

  const notifyStripeError = useNotifyStripeError();

  useEffect(() => {
    if (stripeError) notifyStripeError(stripeError);
  }, [stripeError, notifyStripeError]);

  // ─────────────────────────────────────────────────────────────────────

  return { embeddedPaymentElementView, isUpdatingTotal };
};

// ─── Constants ───────────────────────────────────────────────────────────────

// Not needed for sweetpass as we don't render a payment method label.
const paymentMethods: Parameters<typeof useStripePaymentElement>[0]['paymentMethods'] = [];

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassCheckoutMachine = ReturnType<typeof useSweetpassCheckoutMachine>;

type UseSweetpassPaymentElementParams = Pick<
  SweetpassCheckoutMachine,
  'stripeIntentConfig' | 'stripeElementConfig' | 'setConfirmStripe'
> & {
  amount: number;
};
